import React, { useCallback, useRef, useState } from 'react';
import { getCarteiraPassivo, getCarteiraOutrosPassivos } from '../../../services/carteiraDiariaService';
import { getFundosGerenciados } from '../../../services/fundosGerenciadosService';
import { FundosGerenciadosData } from '../../../types/fundosGerenciados';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Modal from '@mui/material/Modal';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import AppTabs from '../../../components/AppTabs';
import TabPanel from '../../../components/TabPanel';
import { useTableState } from '../../../hooks/useTableState';
import { useSnackbar } from '../../../hooks/useSnackbar';
import CadastroAtivoForm from '../../../components/CadastroAtivoForm';
import SnackbarAlert from '../../../components/SnackbarAlert';
import TableCarteiraDiaria from '../components/TableCarteiraDiaria';

interface PassivoRow {
  data_posicao: string;
  fundoNome: string;
  nome: string;
  valor_total: number;
}

const CarteiraDiaria_Passivos: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isCadastroModalOpen, setIsCadastroModalOpen] = useState<boolean>(false);

  const fundosCacheRef = useRef<FundosGerenciadosData[] | null>(null);

  const passivosTable = useTableState(10);
  const outrosTable   = useTableState(10);
  const { snackbar, showSuccess, showError, close: closeSnackbar } = useSnackbar();

  const [passivos, setPassivos] = useState<PassivoRow[]>([]);
  const [loadingPassivos, setLoadingPassivos] = useState<boolean>(true);
  const [pageCountPassivos, setPageCountPassivos] = useState(0);

  const [outrosPassivos, setOutrosPassivos] = useState<PassivoRow[]>([]);
  const [loadingOutros, setLoadingOutros] = useState<boolean>(true);
  const [pageCountOutros, setPageCountOutros] = useState(0);

  const loadFundos = useCallback(async (): Promise<FundosGerenciadosData[]> => {
    if (fundosCacheRef.current) return fundosCacheRef.current;
    const data = await getFundosGerenciados();
    fundosCacheRef.current = data;
    return data;
  }, []);

  const fetchPassivos = useCallback(async (pageIndex: number, pageSize: number, sortBy: SortingState, globalFilter: string, dataFilter: string) => {
    setLoadingPassivos(true);
    try {
      const [passivoData, fundos] = await Promise.all([
        getCarteiraPassivo(pageIndex, pageSize, sortBy, globalFilter, dataFilter),
        loadFundos(),
      ]);
      setPassivos(passivoData.results.map(r => ({
        data_posicao: r.data_posicao,
        fundoNome: fundos.find(f => f.id === r.fundo)?.razao_social ?? 'Fundo desconhecido',
        nome: r.nome,
        valor_total: r.valor_total,
      })));
      setPageCountPassivos(passivoData.totalPages);
    } catch {
      showError('Erro ao buscar passivos');
    } finally {
      setLoadingPassivos(false);
    }
  }, [loadFundos, showError]);

  const fetchOutrosPassivos = useCallback(async (pageIndex: number, pageSize: number, sortBy: SortingState, globalFilter: string, dataFilter: string) => {
    setLoadingOutros(true);
    try {
      const [outrosData, fundos] = await Promise.all([
        getCarteiraOutrosPassivos(pageIndex, pageSize, sortBy, globalFilter, dataFilter),
        loadFundos(),
      ]);
      setOutrosPassivos(outrosData.results.map(r => ({
        data_posicao: r.data_posicao,
        fundoNome: fundos.find(f => f.id === r.fundo)?.razao_social ?? 'Fundo desconhecido',
        nome: r.descricao,
        valor_total: r.financeiro,
      })));
      setPageCountOutros(outrosData.totalPages);
    } catch {
      showError('Erro ao buscar outros passivos');
    } finally {
      setLoadingOutros(false);
    }
  }, [loadFundos, showError]);

  const handleCadastroSuccess = () => {
    fetchPassivos(passivosTable.pagination.pageIndex, passivosTable.pagination.pageSize, passivosTable.sorting, passivosTable.globalFilter, '');
    fetchOutrosPassivos(outrosTable.pagination.pageIndex, outrosTable.pagination.pageSize, outrosTable.sorting, outrosTable.globalFilter, '');
    showSuccess('Cadastro realizado com sucesso!');
    setIsCadastroModalOpen(false);
  };

  const columns = React.useMemo<ColumnDef<PassivoRow>[]>(() => [
    {
      header: 'Data Posição',
      accessorKey: 'data_posicao',
      cell: (info) => {
        const rawDate = info.getValue() as string;
        const [year, month, day] = rawDate.split('-');
        return `${day}/${month}/${year}`;
      },
      enableSorting: true,
    },
    {
      header: 'Fundo Gerenciado',
      accessorKey: 'fundoNome',
      id: 'fundo__fantasia',
      enableSorting: true,
    },
    { header: 'Nome',        accessorKey: 'nome' },
    { header: 'Valor Total', accessorKey: 'valor_total' },
  ], []);

  return (
    <div>
      <div className="page-header">
        <h2>Carteira Diária Passivos</h2>
        <IconButton onClick={() => setIsCadastroModalOpen(true)}>
          <AddIcon />
        </IconButton>
      </div>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <AppTabs value={activeTab} onChange={(_: React.SyntheticEvent, newValue: number) => setActiveTab(newValue)}>
          <Tab label="Passivos" />
          <Tab label="Outros Passivos" />
        </AppTabs>
      </Box>

      <TabPanel value={activeTab} index={0} sx={{ pt: 2 }}>
        <TableCarteiraDiaria
          columns={columns}
          data={passivos}
          fetchData={fetchPassivos}
          pageCount={pageCountPassivos}
          pagination={passivosTable.pagination}
          setPagination={passivosTable.setPagination}
          sorting={passivosTable.sorting}
          setSorting={passivosTable.setSorting}
          globalFilter={passivosTable.globalFilter}
          setGlobalFilter={passivosTable.setGlobalFilter}
          isLoading={loadingPassivos}
          tipoCarteira="carteira-diaria-passivo"
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1} sx={{ pt: 2 }}>
        <TableCarteiraDiaria
          columns={columns}
          data={outrosPassivos}
          fetchData={fetchOutrosPassivos}
          pageCount={pageCountOutros}
          pagination={outrosTable.pagination}
          setPagination={outrosTable.setPagination}
          sorting={outrosTable.sorting}
          setSorting={outrosTable.setSorting}
          globalFilter={outrosTable.globalFilter}
          setGlobalFilter={outrosTable.setGlobalFilter}
          isLoading={loadingOutros}
          tipoCarteira="carteira-diaria-outros-passivos"
        />
      </TabPanel>

      <Modal open={isCadastroModalOpen} onClose={() => setIsCadastroModalOpen(false)}>
        <div>
          <CadastroAtivoForm
            isOpen={isCadastroModalOpen}
            onRequestClose={() => setIsCadastroModalOpen(false)}
            onCadastroSuccess={handleCadastroSuccess}
            setSnackbarProps={() => {}}
          />
        </div>
      </Modal>
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </div>
  );
};

export default CarteiraDiaria_Passivos;
